<template>
  <div
    :class="{
      [$class('loading')]: !ready,
      [$class('active')]: activeIndex === index
    }"
    :style="{
      ...coordinateStyles,
      transitionDelay: (timings.s * index) + 'ms'
    }"
    class="shop-tag"
    :aria-label="$string('accessibility.open') + ' ' + product.title"
    role="button"
    tabindex="0"
    @click="handleTagClick"
    @keyup="handleTagKeypress"
  >
    <div
      :class="{
        [`shop-tag__chip--${chipPosition}`]: chipPosition !== ''
      }"
      class="shop-tag__chip shop-tag__chip--floating"
      @click="showDrawer"
    >
      <product-chip
        v-if="product && variant && !isMobile"
        :image="variantImage"
        :product-title="product.title"
        :show-price="true"
        :show-variant-title="false"
        :variant="variant"
      />
      <span
        class="shop-tag__plus"
        v-html="$icon('enclosed.plus')"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Shop tag (shop-tag)
 * -----------------------------------------------------------------------------
 * A circle tag with customizable coordinates that shows a product chip on hover (desktop).
 *
 * @param {Number} activeIndex - The index of the active shop tag from parent
 * array.
 * @param {String} chipPosition - The chip position relative to the circle
 * tag. Top, right, bottom, left.
 * @param {Object} coordinates - X and Y positions from theme settings.
 * @param {Object} coordinates.mobile - The mobile positions.
 * @param {Number} coordinates.mobile.x - The horizontal position where 0 is
 * at the left edge of the container.
 * @param {Number} coordinates.mobile.y - The vertical position where 0 is at
 * the top edge of the container.
 * @param {Object} coordinates.desktop - The desktop positions.
 * @param {Number} coordinates.desktop.x - The horizontal position where 0 is
 * at the left edge of the container.
 * @param {Number} coordinates.desktop.y - The vertical position where 0 is at
 * the top edge of the container.
 * @param {Number} index - The index of this shop tag from an array of tags.
 * Start from 1 so the animation delay for the first tag doesn't start from 0.
 * @param {String} overlayNamespace - Namespace of the product form drawer to
 * open on chip press.
 * @param {Object} product - Canvas + GraphQL formatted product.
 * @param {Object} variant - Canvas + GraphQL formatted variant.
 */

import { mapActions, mapState } from 'vuex'
import ProductChip from '~async/bed-visualizer/product-chip'
import timings from '~/config/timings'
import { getVariantImage } from '~/helpers/product'

export default {
  name: 'ShopTag',

  components: {
    ProductChip,
  },

  props: {
    activeIndex: {
      type: Number,
      default: -1,
    },
    chipPosition: {
      type: String,
      default: '',
    },
    coordinates: {
      type: Object,
      default: () => ({}),
    },
    index: {
      type: Number,
      default: 0,
    },
    overlayNamespace: {
      type: String,
      default: '',
    },
    product: {
      type: Object,
      default: () => ({}),
    },
    variant: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['selected-product', 'update-active-index'],

  data() {
    return {
      chipInitialized: false,
      global: true,
      ready: false,
      timings,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute if mobile chip needs to be initialized
     * @returns {boolean}
     */
    initializeChip() {
      return !this.chipInitialized && this.index === 1
    },

    /**
     * Compute the tag positioning styles.
     * @returns {{top: string, left: string}}
     */
    coordinateStyles() {
      const coordinates = this.isMobile ? this.coordinates.mobile : this.coordinates.desktop

      return {
        left: coordinates.x + '%',
        top: coordinates.y + '%',
      }
    },

    /**
     * Compute the current variant image.
     * @returns {Object}
     */
    variantImage() {
      return getVariantImage(this.product, this.variant)
    },
  },

  watch: {

    /**
     * Initialize mobile chip if resizing to mobile.
     */
    isMobile() {
      if (this.initializeChip) {
        this.handleTagClick()
      }
    },

    /**
     * When variant is loaded, initialize the mobile chip.
     */
    variant() {
      if (this.initializeChip) {
        this.handleTagClick()
      }
    },
  },

  mounted() {
    this.loadComponent('main-product')
    setTimeout(() => {
      this.ready = true
    }, timings.l)

    if (this.initializeChip) {
      this.handleTagClick()
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      loadComponent: 'components/load',
      openOverlay: 'overlays/open',
    }),

    /**
     * Show the product form drawer when user presses enter on a shop tag.
     * @param event
     */
    handleTagKeypress(event) {
      if (event.key === 'Enter' && !this.isMobile) {
        this.showDrawer()
      }
    },

    /**
     * Handle tag click for mobile devices. When clicked, update the parent
     * component with the selected variant and product and update the active
     * index.
     */
    handleTagClick() {
      if (!this.isMobile || !this.variant) {
        return
      }
      this.chipInitialized = true
      this.$emit('selected-product', {
        variant: this.variant,
        product: this.product,
      })
      this.$emit('update-active-index', this.index)
    },

    /**
     * Show the PDP in a drawer overlay and send an event to tell the drawer
     * what product + variant to display.
     */
    showDrawer() {
      this.$emit('selected-product', {
        variant: this.variant,
        product: this.product,
      })
      this.openOverlay({
        component: 'main-product',
        ignoreDismissed: true,
        namespace: this.overlayNamespace,
      })
    },
  },
}
</script>
